import { Controller } from "@hotwired/stimulus";

// flatpickr is a datetime picker library. This controller generates the needed HTML and CSS for the calendar to show.
// Read the documentation at https://flatpickr.js.org/
export default class extends Controller {
  static values = {
    useTime: { type: Boolean, default: false },
    use24Hour: { type: Boolean, default: true },
    timeOnly: { type: Boolean, default: false },
    allowInput: { type: Boolean, default: true }
  };

  connect() {
    if (!this.data.has("value") || this.data.get("value") === "") {
      this.setDefaultDate();
    }

    let defaultValue = this.data.get("value");
    defaultValue = new Date(defaultValue);

    this.defaultDateValue = defaultValue;

    if (this.timeOnlyValue) {
      this.useTimeValue = true; // time only implies we want to use time
    }

    const storedFormat = this.getStoredFormat(this.useTimeValue, this.timeOnlyValue);
    const displayedFormat = this.getDisplayFormat(this.useTimeValue, this.use24HourValue, this.timeOnlyValue);

    const locale = this.getLocale();

    const maxDate = this.maxDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const allowInput = this.shouldAllowInput();

    const options = {
      enableTime: this.useTimeValue,
      allowInput: allowInput,
      altInput: true,
      altFormat: displayedFormat,
      dateFormat: storedFormat,
      maxDate: maxDate,
      enable: [
        {
          from: today,
          to: maxDate,
        },
        {
          from: defaultValue,
          to: defaultValue
        }
      ],
      time_24hr: this.use24HourValue,
      defaultDate: defaultValue,
      noCalendar: this.timeOnlyValue,
      minuteIncrement: 1,
      locale: locale,
    };

    if (allowInput) {
      options.onReady = (selectedDates, dateStr, instance) => {
        if (!instance.altInput) return;

        const input = instance.altInput;
        const currentValue = input.value;
        input.placeholder = this.getInputPlaceholder();
        input.addEventListener("focus", () => {
          input.select();
        });

        // When it loses focus we want to use our custom parsing function
        input.addEventListener("blur", () => {
          if (input.value.trim() === "") {
            // Ensure both the visible input and the underlying value are restored
            // to a valid default by using flatpickr's API.
            const fallbackDate = this.defaultDateValue || new Date();
            instance.setDate(fallbackDate, true);
          }
        });
      };

      options.parseDate = this.parseInputDate.bind(this);
    }

    flatpickr(this.element, options);

    this.orderHtmlCorrectly();
  }

  getDisplayFormat(useTime, use24Hour, timeOnly) {
    let displayedFormat = I18n.t("js.flatpickr.date_format");

    if (useTime) {
      displayedFormat += " - ";
      displayedFormat += use24Hour
        ? I18n.t("js.flatpickr.time_format.24_hour_format")
        : I18n.t("js.flatpickr.time_format.12_hour_format");
    }

    if (timeOnly) {
      displayedFormat = use24Hour
        ? I18n.t("js.flatpickr.time_format.24_hour_format")
        : I18n.t("js.flatpickr.time_format.12_hour_format");
    }
    return displayedFormat;
  }

  getStoredFormat(useTime, timeOnly) {
    let storedFormat = "Y-m-d";

    if (useTime) {
      storedFormat += " H:i";
    }

    if (timeOnly) {
      storedFormat = "H:i";
    }
    return storedFormat;
  }

  setDefaultDate() {
    const DEFAULT_TIME = "20:00";

    // This method is used to set the default value of the flatpickr input
    let defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 7); // Adding 7 days

    // .split("T")[0] gives the date in YYYY-MM-DD format and we append DEFAULT_TIME for the time
    const dateWithoutTimeZone = defaultDate.toISOString().split("T")[0];
    this.data.set("value", `${dateWithoutTimeZone} ${DEFAULT_TIME}`);
  }

  getLocale() {
    let locale = I18n.locale;
    const CUSTOM_LOCALES = ["nl", "de", "es", "fr"];

    if (!CUSTOM_LOCALES.includes(locale)) {
      locale = "en";
    }
    return locale;
  }

  maxDate() {
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 5);
    maxDate.setMonth(11);
    maxDate.setDate(31);
    maxDate.setHours(23, 59, 59, 999);
    return maxDate;
  }

  parseInputDate(dateString, format) {
    const trimmed = dateString.trim();

    // US uses mm/dd/yyyy, others use dd/mm/yyyy
    const isUSLocale = this.isUSLocale();

    // Handle time-only inputs (no calendar)
    if (this.timeOnlyValue) {

      // We check for hh:mm or hh:mm am/pm
      const timeMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?:\s*(am|pm|AM|PM))?$/);
      if (timeMatch) {
        const time = this.parseTime(timeMatch[1], timeMatch[2], timeMatch[3]);
        const base = new Date();
        base.setHours(time.hour, time.minute, 0, 0);
        return base;
      }
    }

    // Handle date (with optional time)
    const dateTimeMatch = trimmed.match(
      /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?:\s*(am|pm|AM|PM))?)?$/
    );

    if (dateTimeMatch) {
      let day;
      let monthIndex;
      const year = parseInt(dateTimeMatch[3]);

      if (isUSLocale) {
        // mm/dd/yyyy
        monthIndex = parseInt(dateTimeMatch[1]) - 1;
        day = parseInt(dateTimeMatch[2]);
      } else {
        // dd/mm/yyyy
        day = parseInt(dateTimeMatch[1]);
        monthIndex = parseInt(dateTimeMatch[2]) - 1;
      }

      let hour = 0;
      let minute = 0;
      if (dateTimeMatch[4] && dateTimeMatch[5]) {
        const time = this.parseTime(dateTimeMatch[4], dateTimeMatch[5], dateTimeMatch[6]);
        hour = time.hour;
        minute = time.minute;
      }

      const parsed = new Date(year, monthIndex, day, hour, minute, 0, 0);
      return parsed;
    }

    // If parsing fails (e.g. user typed an invalid string), fall back to the
    // default date instead of clearing the field.
    const parsedDate = flatpickr.parseDate(trimmed, format);
    if (parsedDate instanceof Date && !isNaN(parsedDate)) {
      return parsedDate;
    }

    if (this.defaultDateValue instanceof Date && !isNaN(this.defaultDateValue)) {
      return this.defaultDateValue;
    }

    return new Date();
  }

  parseTime(hour, minute, ampm = null) {
    let formattedHour = parseInt(hour);
    let formattedMinute = parseInt(minute);

    if (ampm) {
      const isPM = ampm.toLowerCase() === "pm";
      if (formattedHour === 12) {
        formattedHour = isPM ? 12 : 0;
      } else if (isPM) {
        formattedHour += 12;
      }
    }

    return { hour: formattedHour, minute: formattedMinute };
  }

  getInputPlaceholder() {
    const datePlaceholder = this.isUSLocale()
      ? I18n.t("js.flatpickr.placeholder.date_us")
      : I18n.t("js.flatpickr.placeholder.date_default");
    const timePlaceholder = this.use24HourValue
      ? I18n.t("js.flatpickr.placeholder.time_24")
      : I18n.t("js.flatpickr.placeholder.time_12");

    if (this.timeOnlyValue) {
      return timePlaceholder;
    }

    if (this.useTimeValue) {
      return `${datePlaceholder} ${timePlaceholder}`;
    }

    return datePlaceholder;
  }

  isUSLocale() {
    const rawLocale = typeof I18n !== "undefined" ? I18n.locale : "en";
    const localeString = (rawLocale || "en").toString();
    return /en[-_]?US/i.test(localeString);
  }

  orderHtmlCorrectly() {
    // This is needed to make sure the styling won't be messed up.
    const input = this.element.nextElementSibling;
    input.classList.remove("active", "form-control", "input");

    // Move the previous sibling to be after this.element
    const previousSibling = this.element.previousElementSibling;
    if (previousSibling) {
      this.element.parentNode.insertBefore(previousSibling, this.element.nextSibling);
    }
  }

  shouldAllowInput() {
    return this.allowInputValue && window.innerWidth > 992;
  }
}
